import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";

import { auth, db } from "../Config/firebase";
import { getUsuarioAtualComTenant, tenantWhere } from "../Utils/tenant";
import {
  getCycleMeta,
  getCycleMetaFromResposta,
  isDateWithinCycle,
  normalizePeriodicidade,
} from "./ciclo.utils";

function getEscopoPesquisa(tipoUsuario) {
  const tipo = String(tipoUsuario || "").toLowerCase();

  if (tipo === "cliente") return "cliente";
  if (tipo === "colaborador") return "colaborador";
  if (tipo === "admin") return "colaborador";

  return "";
}

function normalizarChaveCookie(valor) {
  return String(valor || "geral").replace(/[^a-zA-Z0-9_-]/g, "_");
}

function lerAdiamentoCookie(chave) {
  const prefixo = `${chave}=`;
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(prefixo));
  let adiamentoLocal = 0;

  try {
    adiamentoLocal = Number(localStorage.getItem(chave) || 0);
  } catch (error) {
    console.warn("Não foi possível ler o adiamento da pesquisa no armazenamento local:", error);
  }

  return Math.max(Number(cookie?.slice(prefixo.length) || 0), adiamentoLocal);
}

function salvarAdiamentoCookie(chave, ocultarAte) {
  try {
    localStorage.setItem(chave, String(ocultarAte));
  } catch (error) {
    console.warn("Não foi possível salvar o adiamento da pesquisa no armazenamento local:", error);
  }

  document.cookie = `${chave}=${ocultarAte}; expires=${new Date(
    ocultarAte
  ).toUTCString()}; path=/; SameSite=Lax`;
}

function removerAdiamentoCookie(chave) {
  try {
    localStorage.removeItem(chave);
  } catch (error) {
    console.warn("Não foi possível remover o adiamento da pesquisa do armazenamento local:", error);
  }

  document.cookie = `${chave}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function respostaPertenceAoCiclo(resposta, kpi, cicloAtual) {
  if (resposta.kpiId !== kpi.id) return false;
  if (resposta.ciclo === cicloAtual.chave) return true;
  if (isDateWithinCycle(resposta.createdAt, cicloAtual)) return true;

  const cicloResposta = getCycleMetaFromResposta({
    createdAt: resposta.createdAt,
    ciclo: resposta.ciclo,
    ano: resposta.ano,
    cicloNumero: resposta.cicloNumero ?? resposta.trimestre,
    periodicidade: kpi.periodicidadeCiclos ?? resposta.periodicidadeCiclos,
  });

  return (
    cicloResposta.ano === cicloAtual.ano &&
    cicloResposta.cicloNumero === cicloAtual.cicloNumero
  );
}

export default function PesquisaPendenteAviso({ tipoUsuario }) {
  const history = useHistory();
  const location = useLocation();

  const [usuarioAtual, setUsuarioAtual] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [pendencias, setPendencias] = useState([]);
  const [open, setOpen] = useState(false);
  const [ocultarPorDezDias, setOcultarPorDezDias] = useState(false);
  const [erroAdiamento, setErroAdiamento] = useState("");
  const [chaveAdiamento, setChaveAdiamento] = useState("");

  const escopoPesquisa = useMemo(() => getEscopoPesquisa(tipoUsuario), [tipoUsuario]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUsuarioAtual(user || null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    async function carregarPendencias() {
      try {
        setLoading(true);

        const user = usuarioAtual;
        if (!user || !escopoPesquisa) {
          setPendencias([]);
          setOpen(false);
          return;
        }

        const deveVerificarNesteLogin =
          sessionStorage.getItem("pesquisa_pendente_verificar_login") === "1";
        const jaExibiuNestaSessao =
          sessionStorage.getItem("pesquisa_pendente_exibida_sessao") === "1";

        if (!deveVerificarNesteLogin || jaExibiuNestaSessao) {
          setPendencias([]);
          setOpen(false);
          return;
        }

        if (location.pathname.startsWith("/app/pesquisa/")) {
          setOpen(false);
          return;
        }

        const usuarioTenant = await getUsuarioAtualComTenant();
        const chaveOcultar = `pesquisa_pendente_oculta_ate_${normalizarChaveCookie(
          user.uid
        )}_${normalizarChaveCookie(usuarioTenant.tenantId)}`;
        const ocultarAte = lerAdiamentoCookie(chaveOcultar);

        setChaveAdiamento(chaveOcultar);

        if (ocultarAte > Date.now()) {
          sessionStorage.setItem("pesquisa_pendente_exibida_sessao", "1");
          sessionStorage.removeItem("pesquisa_pendente_verificar_login");
          setPendencias([]);
          setOpen(false);
          return;
        }

        if (ocultarAte) removerAdiamentoCookie(chaveOcultar);

        const kpisSnap = await getDocs(
          query(
            collection(db, "kpis"),
            ...tenantWhere(usuarioTenant),
            where("ativo", "==", true),
            where("tipoKPI", "==", "PESQUISA"),
            where("escopo", "==", escopoPesquisa)
          )
        );

        const kpis = kpisSnap.docs
          .map((item) => ({
            id: item.id,
            ...item.data(),
          }))
          .filter((kpi) => kpi.tipoFormulario !== "PSICOSSOCIAL");

        if (!kpis.length) {
          setPendencias([]);
          setOpen(false);
          return;
        }

        const respostasSnap = await getDocs(
          query(
            collection(db, "kpi_respostas"),
            ...tenantWhere(usuarioTenant),
            where("userId", "==", user.uid)
          )
        );

        const respostas = respostasSnap.docs.map((item) => item.data());

        const pesquisasPendentes = kpis
          .map((kpi) => {
            const periodicidade = normalizePeriodicidade(kpi.periodicidadeCiclos);
            return {
              ...kpi,
              cicloAtual: getCycleMeta(new Date(), periodicidade),
            };
          })
          .filter((kpi) =>
            !respostas.some((resposta) =>
              respostaPertenceAoCiclo(resposta, kpi, kpi.cicloAtual)
            )
          );

        setPendencias(pesquisasPendentes);
        setOcultarPorDezDias(false);
        setErroAdiamento("");

        sessionStorage.setItem("pesquisa_pendente_exibida_sessao", "1");
        sessionStorage.removeItem("pesquisa_pendente_verificar_login");

        if (pesquisasPendentes.length > 0) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch (error) {
        console.error("Erro ao verificar pesquisas pendentes:", error);
        setPendencias([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }

    carregarPendencias();
  }, [escopoPesquisa, location.pathname, usuarioAtual]);

  if (loading || !pendencias.length) return null;

  const pesquisaPrincipal = pendencias[0];
  const textoPendencias =
    pendencias.length > 1
      ? `Voce possui ${pendencias.length} pesquisas pendentes neste ciclo.`
      : "Identificamos que você ainda não respondeu à nossa pesquisa de satisfação.";

  function responderDepois() {
    if (ocultarPorDezDias) {
      const ocultarAte = new Date();
      ocultarAte.setDate(ocultarAte.getDate() + 10);

      const pesquisaForaDoPrazo = pendencias.find(
        (pesquisa) => ocultarAte > pesquisa.cicloAtual.fim
      );

      if (pesquisaForaDoPrazo) {
        setErroAdiamento(
          `Você não pode ocultar por 10 dias porque esse período ultrapassa a data de término da pesquisa "${pesquisaForaDoPrazo.nome}" (${pesquisaForaDoPrazo.cicloAtual.fim.toLocaleDateString("pt-BR")}).`
        );
        return;
      }

      if (chaveAdiamento) {
        salvarAdiamentoCookie(chaveAdiamento, ocultarAte.getTime());
      }
    }

    setErroAdiamento("");
    setOpen(false);
  }

  function responderAgora(kpiId) {
    setErroAdiamento("");
    setOpen(false);
    history.push(`/app/pesquisa/${kpiId}`);
  }

  return (
    <Dialog open={open} onClose={responderDepois} maxWidth="sm" fullWidth>
      <DialogTitle>Bem-vindo(a)!</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Typography>Sua opinião é muito importante para nós.</Typography>

          <Typography>{textoPendencias}</Typography>

          <Typography>
            Ela é rápida e nos ajuda a avaliar a qualidade dos serviços prestados, 
            além de orientar melhorias contínuas.
          </Typography>

          <Typography fontWeight={700}>
            {pendencias.length > 1
              ? "Leva menos de 1 minuto para responder cada uma."
              : "Leva menos de 1 minuto para responder."}
          </Typography>

          {erroAdiamento && (
            <Alert severity="warning">
              {erroAdiamento}
            </Alert>
          )}

          {pendencias.length === 1 ? (
            <Typography variant="body2" color="text.secondary">
              Pesquisa pendente: {pesquisaPrincipal.nome}
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Pesquisas pendentes:
              </Typography>
              <List sx={{ p: 0 }}>
                {pendencias.map((pesquisa) => (
                  <ListItemButton
                    key={pesquisa.id}
                    onClick={() => responderAgora(pesquisa.id)}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemText
                      primary={pesquisa.nome}
                      secondary="Clique para responder agora"
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <FormControlLabel
          sx={{ mr: "auto" }}
          control={
            <Checkbox
              checked={ocultarPorDezDias}
              onChange={(event) => {
                setOcultarPorDezDias(event.target.checked);
                setErroAdiamento("");
              }}
            />
          }
          label="Ocultar este aviso por 10 dias"
        />
        <Button onClick={responderDepois}>Responder depois</Button>
        {pendencias.length === 1 && (
          <Button variant="contained" onClick={() => responderAgora(pesquisaPrincipal.id)}>
            Responder
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
